import { db } from '../config/firebase';
import {
  collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where, Timestamp,
} from 'firebase/firestore';
import { DiscoveredLocation } from '../types/seekFind.types';

const COLLECTION = 'discoveredLocations';

export const seekFindService = {
  async create(data: Omit<DiscoveredLocation, 'id' | 'createdAt'>): Promise<string> {
    const ref = doc(collection(db, COLLECTION));
    // Firestore rejects undefined field values (e.g. accuracy when a spot is
    // tapped rather than GPS, or empty notes), so strip them.
    const clean = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));
    await setDoc(ref, { ...clean, id: ref.id, createdAt: Timestamp.now() });
    return ref.id;
  },

  async getByProject(projectId: string): Promise<DiscoveredLocation[]> {
    const q = query(collection(db, COLLECTION), where('projectId', '==', projectId));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as DiscoveredLocation));
  },

  async attachLead(id: string, leadId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, id), { leadId });
  },

  async attachSurveyResponse(id: string, surveyResponseId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, id), { surveyResponseId });
  },

  // A surveyed door that then signed: keep the survey linkage, flip the
  // outcome to success and attach the lead.
  async markSuccess(id: string, leadId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, id), { outcome: 'success', leadId });
  },

  // Undo-last: remove a logged spot (and its linked survey response, if any).
  async remove(id: string): Promise<void> {
    const ref = doc(db, COLLECTION, id);
    const snap = await getDoc(ref);
    const srId = snap.exists() ? (snap.data() as any).surveyResponseId : undefined;
    if (srId) {
      try { await deleteDoc(doc(db, 'surveyResponses', srId)); } catch { /* best-effort */ }
    }
    await deleteDoc(ref);
  },
};
